import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIncoming, PhoneOutgoing, ArrowLeft, Phone, Settings, Play, Pause, CheckCircle2, Send, Search, CloudUpload, Shield, X, ChevronRight, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface CallsScreenProps {
  onNavigate: (screen: string) => void;
  onDetailView?: (isOpen: boolean) => void;
  simulatedCall?: any;
}
interface Message {
  sender: 'user' | 'ai';
  text: string;
  time: string;
}
interface Call {
  id: number;
  name: string;
  type: 'live' | 'incoming' | 'outgoing';
  time: string;
  duration: string;
  initials: string;
  color: string;
  group: string;
  summary: string;
  messages: Message[];
  isLive?: boolean;
}
export function CallsScreen({
  onNavigate,
  onDetailView,
  simulatedCall
}: CallsScreenProps) {
  const {
    isLight
  } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  const [commandInput, setCommandInput] = useState('');
  const detailScrollRef = useRef<HTMLDivElement>(null);
  const [liveTyping, setLiveTyping] = useState(false);
  const liveSimTimerRef = useRef<NodeJS.Timeout | null>(null);
  const liveSimActiveRef = useRef(false);
  // Backup prompt state
  const [backupPromptShown, setBackupPromptShown] = useState(false);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const [backupStep, setBackupStep] = useState<'ask' | 'provider' | 'frequency' | 'done'>('ask');
  const [backupFrequency, setBackupFrequency] = useState<string | null>(null);
  const [backupProvider, setBackupProvider] = useState<string | null>(null);
  // Call confirmation state
  const [showCallConfirm, setShowCallConfirm] = useState(false);
  // Full conversation script for the live call simulation
  const liveCallScript: Message[] = [{
    sender: 'ai',
    text: "Hello, you've reached the assistant. How can I help you?",
    time: '2:30 PM'
  }, {
    sender: 'user',
    text: 'Hi, is Sarah available? I need to discuss the project deadline.',
    time: '2:30 PM'
  }, {
    sender: 'ai',
    text: "She's currently unavailable. I can take a message or schedule a callback.",
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: "Please tell her to call me back, it's about the Q4 report.",
    time: '2:31 PM'
  }, {
    sender: 'ai',
    text: "Of course. I'll mark this as high priority. Can I get your name?",
    time: '2:31 PM'
  }, {
    sender: 'user',
    text: "It's James from the finance team.",
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: "Got it, James. I'll let Sarah know right away. Is there anything else?",
    time: '2:32 PM'
  }, {
    sender: 'user',
    text: "No, that's all. Thanks!",
    time: '2:32 PM'
  }, {
    sender: 'ai',
    text: "You're welcome. Have a great day!",
    time: '2:32 PM'
  }];
  // Notify parent when detail view opens/closes
  useEffect(() => {
    onDetailView?.(selectedCall !== null);
  }, [selectedCall]);
  // Live call simulation: deliver messages one by one using recursive setTimeout
  useEffect(() => {
    if (!selectedCall?.isLive) {
      // Clean up if we leave the live call
      liveSimActiveRef.current = false;
      if (liveSimTimerRef.current) {
        clearTimeout(liveSimTimerRef.current);
        liveSimTimerRef.current = null;
      }
      return;
    }
    // Prevent double-start (React StrictMode)
    if (liveSimActiveRef.current) return;
    liveSimActiveRef.current = true;
    // Clear messages to start fresh
    setSelectedCall(prev => prev ? {
      ...prev,
      messages: []
    } : prev);
    setLiveTyping(false);
    let currentIndex = 0;
    const deliverNextMessage = () => {
      if (!liveSimActiveRef.current) return;
      if (currentIndex >= liveCallScript.length) {
        // All messages delivered — end the call
        setLiveTyping(false);
        liveSimTimerRef.current = setTimeout(() => {
          if (!liveSimActiveRef.current) return;
          setSelectedCall(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              isLive: false,
              duration: '2m 32s',
              time: '2:32 PM',
              summary: 'James from finance called regarding the Q4 report deadline. Requested a callback from Sarah. Marked as high priority.'
            };
          });
          liveSimActiveRef.current = false;
          // Scroll to summary
          setTimeout(() => {
            detailScrollRef.current?.scrollTo({
              top: detailScrollRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }, 500);
        }, 1500);
        return;
      }
      const typingDelay = currentIndex === 0 ? 800 : 1200;
      const messageDelay = liveCallScript[currentIndex].sender === 'ai' ? 1800 : 1400;
      // Show typing indicator
      liveSimTimerRef.current = setTimeout(() => {
        if (!liveSimActiveRef.current) return;
        setLiveTyping(true);
        // Deliver the message
        liveSimTimerRef.current = setTimeout(() => {
          if (!liveSimActiveRef.current) return;
          setLiveTyping(false);
          const msg = liveCallScript[currentIndex];
          setSelectedCall(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...prev.messages, msg]
            };
          });
          // Auto-scroll
          setTimeout(() => {
            detailScrollRef.current?.scrollTo({
              top: detailScrollRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }, 100);
          currentIndex++;
          // Schedule next message
          deliverNextMessage();
        }, messageDelay);
      }, typingDelay);
    };
    // Start the simulation after a brief initial delay
    liveSimTimerRef.current = setTimeout(() => {
      deliverNextMessage();
    }, 600);
    return () => {
      liveSimActiveRef.current = false;
      if (liveSimTimerRef.current) {
        clearTimeout(liveSimTimerRef.current);
        liveSimTimerRef.current = null;
      }
    };
  }, [selectedCall?.isLive, selectedCall?.id]);
  // Auto-scroll for non-live calls
  useEffect(() => {
    if (selectedCall && !selectedCall.isLive && detailScrollRef.current) {
      setTimeout(() => {
        detailScrollRef.current?.scrollTo({
          top: detailScrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 400);
    }
  }, [selectedCall]);
  const baseCalls: Call[] = [{
    id: 1,
    name: 'Sarah Miller',
    type: 'live',
    time: 'Now',
    duration: '1m 42s',
    initials: 'SM',
    color: isLight ? 'bg-purple-100 text-purple-700' : 'bg-purple-100/60 text-purple-600',
    group: 'Work Contacts',
    isLive: true,
    summary: '',
    messages: []
  }, {
    id: 2,
    name: 'Mike Ross',
    type: 'incoming',
    time: '11:15 AM',
    duration: '2m 14s',
    initials: 'MR',
    color: isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-100/60 text-blue-600',
    group: 'VIP Clients',
    summary: 'Caller scheduled a meeting for tomorrow at 2 PM. Calendar booking confirmed.',
    messages: [{
      sender: 'ai',
      text: 'Thank you for calling. How may I assist you?',
      time: '11:15 AM'
    }, {
      sender: 'user',
      text: "I'd like to schedule a meeting for tomorrow afternoon.",
      time: '11:15 AM'
    }, {
      sender: 'ai',
      text: 'I can check the calendar. What time works best for you?',
      time: '11:16 AM'
    }, {
      sender: 'user',
      text: 'Around 2 PM if possible.',
      time: '11:16 AM'
    }, {
      sender: 'ai',
      text: "Let me check... 2 PM is available. I'll book that for you.",
      time: '11:16 AM'
    }, {
      sender: 'user',
      text: 'Perfect, thanks!',
      time: '11:17 AM'
    }]
  }, {
    id: 3,
    name: '+1 (555) 012-3456',
    type: 'outgoing',
    time: 'Yesterday',
    duration: '45s',
    initials: '#',
    color: isLight ? 'bg-slate-200 text-slate-700' : 'bg-slate-100/60 text-slate-600',
    group: 'All Contacts',
    summary: 'Brief call — wrong number. No action required.',
    messages: [{
      sender: 'ai',
      text: "Hello, you've reached the assistant.",
      time: '4:12 PM'
    }, {
      sender: 'user',
      text: 'Wrong number, sorry.',
      time: '4:12 PM'
    }, {
      sender: 'ai',
      text: 'No problem at all. Have a great day!',
      time: '4:12 PM'
    }]
  }, {
    id: 4,
    name: 'Design Team',
    type: 'incoming',
    time: 'Mon',
    duration: '5m 30s',
    initials: 'DT',
    color: isLight ? 'bg-vexu/20 text-vexu-700' : 'bg-emerald-100/60 text-emerald-600',
    group: 'Work Contacts',
    summary: 'Urgent request for design mockup approval before EOD. Priority notification sent. Client presentation scheduled for tomorrow.',
    messages: [{
      sender: 'ai',
      text: 'Thank you for calling. They are currently unavailable.',
      time: '3:45 PM'
    }, {
      sender: 'user',
      text: 'This is urgent — we need approval on the design mockups before EOD.',
      time: '3:45 PM'
    }, {
      sender: 'ai',
      text: 'I understand this is urgent. Let me try to reach them right away.',
      time: '3:46 PM'
    }, {
      sender: 'user',
      text: 'Please do. The client presentation is tomorrow morning.',
      time: '3:46 PM'
    }, {
      sender: 'ai',
      text: "I've sent a priority notification. They should get back to you shortly.",
      time: '3:47 PM'
    }, {
      sender: 'user',
      text: "Great, I'll wait for their call.",
      time: '3:47 PM'
    }]
  }, {
    id: 5,
    name: 'Mom',
    type: 'incoming',
    time: 'Sun',
    duration: '1m 12s',
    initials: 'M',
    color: isLight ? 'bg-pink-100 text-pink-700' : 'bg-pink-100/60 text-pink-600',
    group: 'Family',
    summary: 'Casual call from family. Requested a callback when available. No urgency.',
    messages: [{
      sender: 'ai',
      text: "Hi! They're not available right now but I can take a message.",
      time: '6:20 PM'
    }, {
      sender: 'user',
      text: "Just tell them to call me when they're free. Nothing urgent.",
      time: '6:20 PM'
    }, {
      sender: 'ai',
      text: "Got it! I'll let them know. Anything else?",
      time: '6:21 PM'
    }]
  }];
  const calls: Call[] = simulatedCall ? [{
    id: 999,
    name: simulatedCall.name,
    type: 'live',
    time: 'Now',
    duration: '0:00',
    initials: simulatedCall.initials,
    color: isLight ? simulatedCall.colorLight : simulatedCall.colorDark,
    group: simulatedCall.group,
    isLive: true,
    summary: '',
    messages: []
  }, ...baseCalls] : baseCalls;
  const filteredCalls = searchQuery.trim() ? calls.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.group.toLowerCase().includes(searchQuery.toLowerCase())) : calls;
  const togglePlay = (idx: number) => {
    if (playingIdx === idx) {
      setPlayingIdx(null);
    } else {
      setPlayingIdx(idx);
      setTimeout(() => setPlayingIdx(null), 2000);
    }
  };
  const handleSelectCall = (call: Call) => {
    // Stop any existing simulation
    liveSimActiveRef.current = false;
    if (liveSimTimerRef.current) {
      clearTimeout(liveSimTimerRef.current);
      liveSimTimerRef.current = null;
    }
    setSelectedCall(call);
    setPlayingIdx(null);
    setCommandInput('');
    setLiveTyping(false);
  };
  const handleBack = () => {
    liveSimActiveRef.current = false;
    if (liveSimTimerRef.current) {
      clearTimeout(liveSimTimerRef.current);
      liveSimTimerRef.current = null;
    }
    setLiveTyping(false);
    // Show backup prompt on first back press
    if (!backupPromptShown) {
      setShowBackupPrompt(true);
      setBackupStep('ask');
      return;
    }
    setSelectedCall(null);
  };
  const handleBackupEnable = () => {
    setBackupStep('provider');
  };
  const handleBackupProviderSelect = (provider: string) => {
    setBackupProvider(provider);
    setBackupStep('frequency');
  };
  const handleBackupFrequencySelect = (freq: string) => {
    setBackupFrequency(freq);
    setBackupStep('done');
    setTimeout(() => {
      setShowBackupPrompt(false);
      setBackupPromptShown(true);
      setSelectedCall(null);
    }, 1500);
  };
  const handleBackupSkip = () => {
    setShowBackupPrompt(false);
    setBackupPromptShown(true);
    setSelectedCall(null);
  };
  const handleSendCommand = () => {
    if (!commandInput.trim() || !selectedCall) return;
    // Add the command as a new user message to the selected call's messages
    const newMsg: Message = {
      sender: 'user',
      text: commandInput.trim(),
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    setSelectedCall({
      ...selectedCall,
      messages: [...selectedCall.messages, newMsg]
    });
    setCommandInput('');
    // Simulate AI response after 1s
    setTimeout(() => {
      setSelectedCall(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: [...prev.messages, {
            sender: 'ai',
            text: "Got it, I'll take care of that right away.",
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })
          }]
        };
      });
    }, 1000);
  };
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-993">
      {/* Call List View */}
      <AnimatePresence data-id="element-994">
        {!selectedCall && <motion.div key="list" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -40
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 flex flex-col" data-id="element-995">
            {/* Header */}
            <div className={`px-5 pt-6 pb-3 relative z-10 ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-996">
              <div className="flex items-center justify-between mb-3" data-id="element-997">
                <h1 className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-998">
                  Calls
                </h1>
                <button onClick={() => onNavigate('callerExperience')} className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20 hover:bg-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20 hover:bg-vexu/20'}`} data-id="element-999">
                  <Users className="w-3.5 h-3.5" data-id="element-1000" />
                  Caller Experience
                </button>
              </div>
              {/* Search Bar */}
              <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl transition-colors ${isLight ? 'bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]' : 'bg-[#141414] border border-white/[0.06]'}`} data-id="element-1001">
                <Search className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1002" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search calls..." className={`flex-1 text-[13px] bg-transparent outline-none ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} data-id="element-1003" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${isLight ? 'text-slate-400 hover:text-slate-600 bg-slate-100' : 'text-zinc-500 hover:text-zinc-300 bg-white/5'}`} data-id="element-1004">
                    Clear
                  </button>}
              </div>
            </div>

            {/* Full-width Call List */}
            <div className="flex-1 overflow-y-auto pb-24 relative z-10" data-id="element-1005">
              {filteredCalls.length === 0 ? <div className="flex flex-col items-center justify-center py-16" data-id="element-1006">
                  <Search className={`w-8 h-8 mb-3 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1007" />
                  <p className={`text-sm font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1008">
                    No calls found
                  </p>
                </div> : filteredCalls.map((call, idx) => <motion.button key={call.id} initial={call.id === 999 ? {
            opacity: 0,
            y: -20
          } : false} animate={call.id === 999 ? {
            opacity: 1,
            y: 0
          } : undefined} transition={call.id === 999 ? {
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94]
          } : undefined} style={call.id === 999 ? {
            willChange: 'transform, opacity'
          } : undefined} onClick={() => handleSelectCall(call)} className={`w-full px-5 py-3.5 flex items-center gap-3.5 transition-colors ${isLight ? 'hover:bg-white/80 active:bg-white' : 'hover:bg-white/[0.02] active:bg-white/[0.04]'} ${idx < filteredCalls.length - 1 ? isLight ? 'border-b border-slate-100/80' : 'border-b border-white/[0.04]' : ''}`} data-id="element-1009">
                    <div className="relative" data-id="element-1010">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[13px] flex-shrink-0 ${call.color}`} data-id="element-1011">
                        {call.initials}
                      </div>
                      {call.isLive && <div className="absolute -top-0.5 -right-0.5" data-id="element-1012">
                          <span className={`inline-flex rounded-full h-3 w-3 bg-vexu border-2 ${isLight ? 'border-slate-50' : 'border-[#0A0A0A]'}`} data-id="element-1013" />
                        </div>}
                    </div>
                    <div className="flex-1 text-left min-w-0" data-id="element-1014">
                      <div className="flex items-center gap-2" data-id="element-1015">
                        <span className={`text-[14px] font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1016">
                          {call.name}
                        </span>
                        {call.isLive && <span className={`text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-[5px] flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20'}`} data-id="element-1017">
                            Live
                          </span>}
                      </div>
                      <div className={`flex items-center gap-1.5 text-[12px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1018">
                        {call.isLive ? <>
                            <span className="w-1.5 h-1.5 rounded-full bg-vexu flex-shrink-0" data-id="element-1019" />
                            <span className={`font-medium ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1020">
                              {call.duration}
                            </span>
                          </> : <>
                            {call.type === 'incoming' && <PhoneIncoming className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1021" />}
                            {call.type === 'outgoing' && <PhoneOutgoing className={`w-3 h-3 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1022" />}
                            <span data-id="element-1023">{call.group}</span>
                            <span className={`${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1024">
                              ·
                            </span>
                            <span data-id="element-1025">{call.duration}</span>
                          </>}
                      </div>
                    </div>
                    <div className={`text-[11px] font-medium flex-shrink-0 ${call.isLive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1026">
                      {call.time}
                    </div>
                  </motion.button>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Call Detail View */}
      <AnimatePresence data-id="element-1027">
        {selectedCall && <motion.div key="detail" initial={{
        opacity: 0,
        x: 40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 40
      }} transition={{
        duration: 0.25
      }} className={`absolute inset-0 z-20 flex flex-col ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-1028">
            {/* WhatsApp Header */}
            <div className={`px-4 pt-6 pb-3 flex items-center gap-3 ${isLight ? 'bg-white border-b border-slate-200' : 'bg-[#141414] border-b border-white/5'}`} data-id="element-1029">
              <button onClick={() => handleBack()} className={`p-1 rounded-full ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1030">
                <ArrowLeft className="w-5 h-5" data-id="element-1031" />
              </button>
              <div className="relative" data-id="element-1032">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selectedCall.color}`} data-id="element-1033">
                  <span className="text-xs font-bold" data-id="element-1034">
                    {selectedCall.initials}
                  </span>
                </div>
                {selectedCall.isLive && <div className="absolute -top-0.5 -right-0.5" data-id="element-1035">
                    <span className="inline-flex rounded-full h-2.5 w-2.5 bg-vexu border-2 border-white dark:border-[#141414]" data-id="element-1036" />
                  </div>}
              </div>
              <div className="flex-1 min-w-0" data-id="element-1037">
                <div className="flex items-center gap-2" data-id="element-1038">
                  <h2 className={`text-sm font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1039">
                    {selectedCall.name}
                  </h2>
                  {selectedCall.isLive && <span className={`text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-md ${isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/10 text-vexu border border-vexu/20'}`} data-id="element-1040">
                      Live
                    </span>}
                </div>
                <p className={`text-[11px] ${selectedCall.isLive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1041">
                  {selectedCall.isLive ? `Live · ${selectedCall.duration}` : `${selectedCall.group} · ${selectedCall.time}`}
                </p>
              </div>
              <div className="flex items-center gap-1" data-id="element-1042">
                <button onClick={() => setShowCallConfirm(true)} className={`p-2 rounded-full ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/5'}`} data-id="element-1043">
                  <Phone className="w-4 h-4" data-id="element-1044" />
                </button>
              </div>
            </div>

            {/* Live Call Banner */}
            {selectedCall.isLive && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} className={`px-4 py-2.5 flex items-center justify-between ${isLight ? 'bg-vexu/5 border-b border-vexu/10' : 'bg-vexu/[0.03] border-b border-vexu/10'}`} data-id="element-1045">
                <div className="flex items-center gap-2" data-id="element-1046">
                  <div className="flex items-center gap-1.5" data-id="element-1047">
                    <span className="w-1.5 h-1.5 rounded-full bg-vexu" data-id="element-1048" />
                    <span className={`text-[10px] font-semibold uppercase tracking-widest ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1049">
                      Active Call
                    </span>
                  </div>
                  <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1050">
                    ·
                  </span>
                  <span className={`text-[10px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1051">
                    AI Agent handling
                  </span>
                </div>
                <div className="flex items-center gap-0.5" data-id="element-1052">
                  {[...Array(4)].map((_, i) => <motion.div key={i} animate={{
              height: ['3px', '10px', '5px', '8px', '3px']
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              delay: i * 0.15
            }} className="w-[2px] rounded-full bg-vexu/60" data-id="element-1053" />)}
                </div>
              </motion.div>}

            {/* Scrollable Content */}
            <div ref={detailScrollRef} className="flex-1 overflow-y-auto pb-20" data-id="element-1054">
              {/* Date pill */}
              <div className="flex justify-center py-3" data-id="element-1055">
                <span className={`text-[10px] font-medium px-3 py-1 rounded-full ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/5 text-zinc-500'}`} data-id="element-1056">
                  {selectedCall.isLive ? 'Now' : selectedCall.time === 'Yesterday' || selectedCall.time === 'Mon' || selectedCall.time === 'Sun' ? selectedCall.time : 'Today'}
                </span>
              </div>

              {/* Chat Thread */}
              <div className="px-5 space-y-1.5 pb-4" data-id="element-1057">
                {selectedCall.messages.map((msg, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.05,
              duration: 0.2
            }} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1058">
                    <div className={`max-w-[70%] rounded-[14px] px-3 py-2 ${msg.sender === 'user' ? 'bg-vexu text-white rounded-tr-[4px]' : isLight ? 'bg-white text-slate-800 rounded-tl-[4px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-slate-100/80' : 'bg-[#1A1A1A] text-zinc-100 rounded-tl-[4px] border border-white/[0.04]'}`} data-id="element-1059">
                      <div className="flex items-center gap-2" data-id="element-1060">
                        <button onClick={() => togglePlay(idx)} className={`flex-shrink-0 w-5.5 h-5.5 w-[22px] h-[22px] rounded-full flex items-center justify-center transition-colors ${playingIdx === idx ? msg.sender === 'user' ? 'bg-white/20 text-white' : 'bg-vexu text-white' : msg.sender === 'user' ? 'bg-white/10 text-white/70' : isLight ? 'bg-slate-50 text-slate-400 border border-slate-100' : 'bg-white/[0.06] text-zinc-500'}`} data-id="element-1061">
                          {playingIdx === idx ? <div className="w-2 h-2 bg-current rounded-[2px]" data-id="element-1062" /> : <Play className="w-2.5 h-2.5 ml-[1px]" data-id="element-1063" />}
                        </button>
                        <div className="flex-1 min-w-0 relative" data-id="element-1064">
                          {/* Text always rendered to maintain bubble size */}
                          <motion.p animate={{
                      opacity: playingIdx === idx ? 0 : 1
                    }} transition={{
                      duration: 0.2
                    }} className="text-[12px] leading-[1.45] tracking-[-0.01em]" data-id="element-1065">
                            {msg.text}
                          </motion.p>
                          {/* Waveform overlay */}
                          <AnimatePresence data-id="element-1066">
                            {playingIdx === idx && <motion.div initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} exit={{
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="absolute inset-0 flex items-center gap-[2px]" data-id="element-1067">
                                {[...Array(28)].map((_, i) => <motion.div key={i} animate={{
                          height: ['15%', '100%', '35%', '75%', '15%']
                        }} transition={{
                          duration: 0.5 + Math.random() * 0.5,
                          repeat: Infinity
                        }} className={`w-[2px] rounded-full ${msg.sender === 'user' ? 'bg-white/35' : 'bg-vexu/40'}`} data-id="element-1068" />)}
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>
                      <div className={`flex items-center justify-end gap-1 mt-0.5 ${msg.sender === 'user' ? 'text-white/40' : isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1069">
                        <span className="text-[9px] tracking-wide" data-id="element-1070">
                          {msg.time}
                        </span>
                      </div>
                    </div>
                  </motion.div>)}

                {/* Live typing indicator */}
                {selectedCall.isLive && liveTyping && <motion.div initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.15
            }} className="flex justify-start" data-id="element-1071">
                    <div className={`rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5 ${isLight ? 'bg-white border border-slate-100/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]' : 'bg-[#1A1A1A] border border-white/[0.04]'}`} data-id="element-1072">
                      <div className="flex gap-[3px] items-center" data-id="element-1073">
                        {[0, 0.15, 0.3].map((delay, i) => <motion.div key={i} animate={{
                    opacity: [0.3, 0.8, 0.3]
                  }} transition={{
                    duration: 0.9,
                    repeat: Infinity,
                    delay
                  }} className={`w-[5px] h-[5px] rounded-full ${isLight ? 'bg-slate-300' : 'bg-zinc-600'}`} data-id="element-1074" />)}
                      </div>
                    </div>
                  </motion.div>}
              </div>

              {/* AI Summary Card - only for completed calls */}
              {!selectedCall.isLive && <div className="px-4" data-id="element-1075">
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: selectedCall.messages.length * 0.07 + 0.2
            }} className={`rounded-2xl p-5 ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-[#141414] border border-white/5'}`} data-id="element-1076">
                    <div className="flex items-center gap-2 mb-3" data-id="element-1077">
                      <div className="w-7 h-7 rounded-full bg-vexu/15 flex items-center justify-center" data-id="element-1078">
                        <CheckCircle2 className="w-3.5 h-3.5 text-vexu" data-id="element-1079" />
                      </div>
                      <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1080">
                        Call Summary
                      </h3>
                    </div>
                    <div className={`flex items-center gap-4 mb-3 pb-3 border-b ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-1081">
                      <div data-id="element-1082">
                        <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1083">
                          Duration
                        </p>
                        <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1084">
                          {selectedCall.duration}
                        </p>
                      </div>
                      <div data-id="element-1085">
                        <p className={`text-[10px] uppercase tracking-wider font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1086">
                          Group
                        </p>
                        <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1087">
                          {selectedCall.group}
                        </p>
                      </div>
                    </div>
                    <p className={`text-xs leading-relaxed mb-3 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1088">
                      {selectedCall.summary}
                    </p>
                    {/* Play Full Call Button */}
                    <button onClick={() => setPlayingIdx(playingIdx === -1 ? null : -1)} className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all ${playingIdx === -1 ? isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20' : isLight ? 'bg-slate-50 border border-slate-100 hover:bg-slate-100/80' : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-1089">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${playingIdx === -1 ? 'bg-vexu text-white' : isLight ? 'bg-white text-slate-500 shadow-sm border border-slate-100' : 'bg-white/10 text-zinc-400'}`} data-id="element-1090">
                        {playingIdx === -1 ? <Pause className="w-3.5 h-3.5" data-id="element-1091" /> : <Play className="w-3.5 h-3.5 ml-0.5" data-id="element-1092" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1093">
                        {playingIdx === -1 ? <div className="flex items-center gap-[2px] h-5" data-id="element-1094">
                            {[...Array(32)].map((_, i) => <motion.div key={i} animate={{
                      height: ['15%', '100%', '35%', '75%', '15%']
                    }} transition={{
                      duration: 0.5 + Math.random() * 0.5,
                      repeat: Infinity
                    }} className="w-[2px] rounded-full bg-vexu/50" data-id="element-1095" />)}
                          </div> : <span className={`text-[12px] font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1096">
                            Play Full Call
                          </span>}
                      </div>
                      <span className={`text-[10px] font-medium ${playingIdx === -1 ? 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1097">
                        {selectedCall.duration}
                      </span>
                    </button>
                  </motion.div>
                </div>}
            </div>

            {/* Floating Command Input Bar */}
            <div className="absolute bottom-5 left-4 right-4 z-30" data-id="element-1098">
              <div className={`flex gap-2 p-2 rounded-full shadow-lg backdrop-blur-xl ${isLight ? 'bg-white/80 border border-slate-200/60 shadow-slate-200/50' : 'bg-[#141414]/80 border border-white/10 shadow-black/40'}`} data-id="element-1099">
                <input type="text" value={commandInput} onChange={e => setCommandInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSendCommand()} placeholder="Send a command to agent..." className={`flex-1 rounded-full px-4 py-2 text-sm outline-none bg-transparent ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} data-id="element-1100" />
                <button onClick={handleSendCommand} disabled={!commandInput.trim()} className="w-9 h-9 rounded-full bg-vexu text-white flex items-center justify-center disabled:opacity-40 transition-all flex-shrink-0" data-id="element-1101">
                  <Send className="w-4 h-4" data-id="element-1102" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Call Confirmation Modal */}
      <AnimatePresence data-id="element-1103">
        {showCallConfirm && selectedCall && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1104">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCallConfirm(false)} data-id="element-1105" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1106">
              <div className="p-6 space-y-5" data-id="element-1107">
                <div className="flex items-start justify-between" data-id="element-1108">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-1109">
                    <Phone className={`w-6 h-6 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1110" />
                  </div>
                  <button onClick={() => setShowCallConfirm(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-1111">
                    <X className="w-4 h-4" data-id="element-1112" />
                  </button>
                </div>

                <div data-id="element-1113">
                  <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1114">
                    Call {selectedCall.name}?
                  </h3>
                  <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1115">
                    This will open your phone dialer to call this contact
                    directly.
                  </p>
                </div>

                {/* Contact info card */}
                <div className={`flex items-center gap-3 p-3.5 rounded-xl ${isLight ? 'bg-slate-50 border border-slate-100' : 'bg-white/[0.03] border border-white/[0.06]'}`} data-id="element-1116">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0 ${selectedCall.color}`} data-id="element-1117">
                    {selectedCall.initials}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1118">
                    <p className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1119">
                      {selectedCall.name}
                    </p>
                    <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1120">
                      {selectedCall.group}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-1" data-id="element-1121">
                  <button onClick={() => setShowCallConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-1122">
                    Cancel
                  </button>
                  <button onClick={() => {
                setShowCallConfirm(false);
                window.open('tel:+15550000000', '_self');
              }} className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-1123">
                    <Phone className="w-4 h-4" data-id="element-1124" />
                    Call Now
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Cloud Backup Prompt Overlay */}
      <AnimatePresence data-id="element-1125">
        {showBackupPrompt && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-1126">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleBackupSkip} data-id="element-1127" />
            <motion.div initial={{
          y: 120,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 120,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 28
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1128">
              <AnimatePresence mode="wait" data-id="element-1129">
                {/* Step 1: Ask to enable backup */}
                {backupStep === 'ask' && <motion.div key="ask" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-5" data-id="element-1130">
                    <div className="flex items-start justify-between" data-id="element-1131">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-1132">
                        <CloudUpload className={`w-6 h-6 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1133" />
                      </div>
                      <button onClick={handleBackupSkip} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-1134">
                        <X className="w-4 h-4" data-id="element-1135" />
                      </button>
                    </div>

                    <div data-id="element-1136">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1137">
                        Back up your calls
                      </h3>
                      <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1138">
                        Keep your call recordings, transcripts, and AI summaries
                        safe in the cloud. You can restore them anytime.
                      </p>
                    </div>

                    <div className={`flex items-center gap-3 p-3 rounded-xl ${isLight ? 'bg-slate-50' : 'bg-white/[0.03]'}`} data-id="element-1139">
                      <Shield className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1140" />
                      <p className={`text-[11px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1141">
                        Your data is encrypted end-to-end and stored securely on
                        your cloud account.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-1" data-id="element-1142">
                      <button onClick={handleBackupSkip} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors ${isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`} data-id="element-1143">
                        Not now
                      </button>
                      <button onClick={handleBackupEnable} className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-1144">
                        Enable Backup
                      </button>
                    </div>
                  </motion.div>}

                {/* Step 2: Choose cloud provider */}
                {backupStep === 'provider' && <motion.div key="provider" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-4" data-id="element-1145">
                    <div data-id="element-1146">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1147">
                        Choose cloud account
                      </h3>
                      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1148">
                        Where should we store your call backups?
                      </p>
                    </div>

                    <div className="space-y-2.5" data-id="element-1149">
                      {/* Google Drive */}
                      <button onClick={() => handleBackupProviderSelect('google')} className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border transition-all active:scale-[0.98] ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-1150">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-100" data-id="element-1151">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-1152">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-1153" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-1154" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-1155" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-1156" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left" data-id="element-1157">
                          <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1158">
                            Google Drive
                          </p>
                          <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1159">
                            alex.founder@gmail.com
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1160" />
                      </button>

                      {/* iCloud */}
                      <button onClick={() => handleBackupProviderSelect('icloud')} className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border transition-all active:scale-[0.98] ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`} data-id="element-1161">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-b from-[#5AC8FA] to-[#007AFF] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-1162">
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1163">
                            <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" data-id="element-1164" />
                          </svg>
                        </div>
                        <div className="flex-1 text-left" data-id="element-1165">
                          <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1166">
                            iCloud
                          </p>
                          <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1167">
                            alex@icloud.com
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1168" />
                      </button>
                    </div>

                    <button onClick={handleBackupSkip} className={`w-full py-2.5 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-1169">
                      Skip for now
                    </button>
                  </motion.div>}

                {/* Step 3: Choose frequency */}
                {backupStep === 'frequency' && <motion.div key="frequency" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="p-6 space-y-4" data-id="element-1170">
                    <div data-id="element-1171">
                      <h3 className={`text-lg font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1172">
                        Backup frequency
                      </h3>
                      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1173">
                        How often should we back up your call data?
                      </p>
                    </div>

                    <div className="space-y-1.5" data-id="element-1174">
                      {[{
                  label: 'Daily',
                  desc: 'Every night at 2:00 AM',
                  value: 'daily'
                }, {
                  label: 'Weekly',
                  desc: 'Every Sunday at 2:00 AM',
                  value: 'weekly'
                }, {
                  label: 'Monthly',
                  desc: 'First day of each month',
                  value: 'monthly'
                }, {
                  label: 'Only when I tap "Back up"',
                  desc: 'Manual backups only',
                  value: 'manual'
                }].map(option => <button key={option.value} onClick={() => handleBackupFrequencySelect(option.value)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-colors ${isLight ? 'hover:bg-slate-50 active:bg-slate-100' : 'hover:bg-white/[0.03] active:bg-white/[0.06]'}`} data-id="element-1175">
                          <div data-id="element-1176">
                            <p className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1177">
                              {option.label}
                            </p>
                            <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1178">
                              {option.desc}
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`} data-id="element-1179">
                            <div className="w-2 h-2 rounded-full" data-id="element-1180" />
                          </div>
                        </button>)}
                    </div>

                    <button onClick={handleBackupSkip} className={`w-full py-2.5 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-1181">
                      Skip for now
                    </button>
                  </motion.div>}

                {/* Step 3: Done */}
                {backupStep === 'done' && <motion.div key="done" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="p-8 flex flex-col items-center text-center" data-id="element-1182">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: 0.1
              }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-1183">
                      <CheckCircle2 className={`w-8 h-8 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1184" />
                    </motion.div>
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1185">
                      Backup enabled
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1186">
                      {backupFrequency === 'daily' && `Backed up daily to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'weekly' && `Backed up weekly to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'monthly' && `Backed up monthly to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                      {backupFrequency === 'manual' && `Manual backup to ${backupProvider === 'google' ? 'Google Drive' : 'iCloud'}`}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}